import { FC } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';

import { CopyrightFeeTooltip } from '../components/CopyrightFeeTooltip';
import { Image } from '../components/Image';
import { PriceRow } from '../components/PriceRow';
import { ORDER_CART_ENTRY_ID, ORDER_CART_TEST_ID } from '../constants';
import { ImageData, Price } from '../model';
import {
    CellDetailSecondary,
    CellTitle,
    FootNote,
    LocalCode,
    MainProductEntryListItem,
    Separator,
} from './entries.styles';

type MainProductEntryProps = {
    name: string;
    image: ImageData;
    variantOptions: string;
    monthlyPrice?: Price;
    fullPrice?: Price;
    localCode?: string;
    initialPayment?: Price;
    residualPrice?: Price;
    copyrightFee?: Price;
    discount?: Price;
    footNote?: string;
};

export const MainProductEntry: FC<MainProductEntryProps> = ({
    name,
    image,
    variantOptions,
    localCode,
    footNote,
    fullPrice,
    residualPrice,
    discount,
    monthlyPrice,
    initialPayment,
    copyrightFee,
}) => {
    const { t } = useTranslations();

    return (
        <MainProductEntryListItem data-test={ORDER_CART_ENTRY_ID.MAIN_PRODUCT} aria-label={name}>
            <Image url={image.url} alt={image.alt} />
            <CellTitle data-test={ORDER_CART_TEST_ID.ENTRY_TITLE_MAIN_PRODUCT}>{name}</CellTitle>
            <CellDetailSecondary>{variantOptions}</CellDetailSecondary>

            <Separator />

            {monthlyPrice?.formattedValue ? (
                <PriceRow
                    term={t['order_cart.entry_main_product.monthly_price']}
                    price={monthlyPrice}
                    isMonthly={true}
                    dataTest={ORDER_CART_TEST_ID.PRICE_MAIN_PRODUCT_MONTHLY}
                />
            ) : (
                <PriceRow
                    term={t['order_cart.entry_main_product.full_price']}
                    price={fullPrice}
                    dataTest={ORDER_CART_TEST_ID.PRICE_MAIN_PRODUCT_TOTAL}
                />
            )}

            <LocalCode>{localCode}</LocalCode>

            <PriceRow
                term={t['order_cart.entry_main_product.residual_price']}
                price={residualPrice}
                dataTest={ORDER_CART_TEST_ID.PRICE_MAIN_PRODUCT_RESIDUAL}
            />
            <PriceRow
                term={t['order_cart.entry_main_product.initial_payment']}
                price={initialPayment}
                dataTest={ORDER_CART_TEST_ID.PRICE_MAIN_PRODUCT_INITIAL}
            />
            <PriceRow
                term={<CopyrightFeeTooltip />}
                price={copyrightFee}
                dataTest={ORDER_CART_TEST_ID.PRICE_MAIN_PRODUCT_COPYRIGHT_FEE}
            />
            <PriceRow
                term={t['order_cart.entry_main_product.discount_price']}
                price={discount}
                isDiscount={true}
                dataTest={ORDER_CART_TEST_ID.PRICE_MAIN_PRODUCT_DISCOUNT}
            />

            {!!footNote && <FootNote>{footNote}</FootNote>}
        </MainProductEntryListItem>
    );
};
